import React, { useState } from 'react';
import theme from '../styles/theme';
import styled from 'styled-components';

import { Flex, StyledText } from './index';
import { StyledTextVariantsTypeV2 } from './StyledText';

import { translate } from '../translations/translations';
import { isNotNullOrUndefined } from 'src/utils/helpers';
import { IPipeResult, pipe } from 'src/utils/validators/pipe';
import { IValidationKey, IValidatorErrorCode, IValidatorOptions } from 'src/utils/validators';

type InputType = 'text' | 'email' | 'password' | 'time';

interface IProps {
    value?: number | string;
    defaultValue?: number | string;
    label?: string;
    placeholder?: string;
    height?: string;
    onChange?: (value: string) => void;
    validate?: (value: boolean) => void;
    required?: boolean;
    disabled?: boolean;
    width?: string;
    margin?: string;
    validators?: Array<IValidationKey | { key: IValidationKey; options: IValidatorOptions }>;
    type?: InputType;
    fontColor?: any;
}

const InputComponentContainer = styled.div<IProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    width: ${(props) => (props.width ? props.width : '100%')};
    margin: ${(props) => (props.margin ? props.margin : 0)};
    height: auto;
    border: 1px solid #e1e1e1;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.06);
    border-radius: 4px;

    textarea {
        height: ${(props) => (props.height ? props.height : 'auto')};
        width: calc(100% - 32px);
        padding: 0 12px;
        border: none;
        color: ${(props) => (props.fontColor ? props.fontColor : theme.constantStyles.colorStyles.black)};

        font-family: Mulish-Regular;
        font-style: normal;
        font-weight: bold;
        font-size: 14px;
        &:disabled {
            opacity: 0.5;
        }

        &::placeholder {
            color: ${theme.colors.light.grayscale['400']};
        }
    }

    textarea:focus {
        outline: none;
        border: none;
    }

    textareas:hover {
        border: 1px solid ${theme.colors.light.grayscale['400']};
    }

    @media only screen and (max-width: 768px) {
        margin-bottom: 10px !important;
        width: 100%;
    }

    @media only screen and (max-width: 480px) {
        margin-bottom: 10px !important;
        width: 100%;
    }
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
    height: auto;

    i {
        position: absolute;
        top: 50%;
        right: 5%;
        -ms-transform: translateY(-50%);
        transform: translateY(-50%);
        color: ${theme.colors.light.grayscale['400']};
        cursor: pointer;
    }
`;

const LabelWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    margin-left: 24px;
    width: 100%;
    padding-top: 12px;
    padding-bottom: 6px;
    font-style: normal;
    font-weight: bold;
    font-size: 9px;
    line-height: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    min-width: 150px;
    color: ${theme.colors.light.grayscale[300]};

    & > div,
    span {
        display: inline;
    }
`;

const AsteriskSign = styled.div`
    color: ${theme.colors.light.red.baseLight};
    padding: 0 2%;
    cursor: pointer;
    height: 15px;
`;

const Textarea = (props: IProps) => {
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState<string | number | null>('');
    const [isValid, setIsValid] = useState<boolean>(true);
    const [validationError, setValidationError] = useState<
        Array<{
            key: IValidatorErrorCode;
            options?: IValidatorOptions;
        }>
    >();

    function onFocus() {
        setFocused(true);
    }

    function onBlur() {
        setFocused(false);
    }

    function validateValue(validationResult: IPipeResult) {
        if (props.validate) {
            props.validate(validationResult.isValid);
        }
        if (props.onChange) {
            props.onChange(validationResult.value);
        }
        setValidationError(validationResult.errors);
        setIsValid(validationResult.isValid);
    }

    const onChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { validators } = props;
        setValue(evt.target.value);
        const validationResult = pipe(evt.target.value, validators);
        validateValue(validationResult);
    };

    return (
        <InputComponentContainer width={props.width} margin={props.margin} height={props.height}>
            {isNotNullOrUndefined(props.label) && (
                <LabelWrapper>
                    <Flex alignItems="center" width="100%">
                        <StyledText text={props.label} variant={StyledTextVariantsTypeV2.INPUT_TITLE} />
                        {props.required && <AsteriskSign>&#x2a;</AsteriskSign>}
                    </Flex>
                </LabelWrapper>
            )}
            <InputWrapper>
                <textarea
                    value={value ? value?.toString() : props.value}
                    defaultValue={props?.defaultValue}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    style={{
                        color: props.fontColor,
                        resize: 'none',
                        height: props.height,
                        borderBottomColor: !isValid
                            ? theme.colors.light.red.baseLight
                            : focused
                            ? theme.colors.light.green.baseLight
                            : theme.colors.light.grayscale['400'],
                    }}
                />
            </InputWrapper>
            {validationError && !isValid && (
                <StyledText
                    text={translate(`errors.${validationError[0].key}`)}
                    colorVariant={theme.colors.light.red.baseLight}
                    variant={StyledTextVariantsTypeV2.PARAGRAPH_4}
                    margin={'10px 0 0 0'}
                    width={'100%'}
                />
            )}
        </InputComponentContainer>
    );
};

export default Textarea;
